import {TKey} from '@/translations/i18n'
import {
    KnownExclusionReason,
    SiarBulkExportType,
    SiarExportStatus,
    SiarExportTransmissionStatus,
    SiarExportsColumn,
    SiarPreviewItemProgressStatus
} from '@/features/projects/siar-bulk-export/types'

export const siarExportTypeToLabel = {
    open_project: 'projects:bulk_siar_export_type_open',
    close_project: 'projects:bulk_siar_export_type_close'
} as const satisfies Record<SiarBulkExportType, TKey>

export const siarExportTypeToNoEligibleProjectsLabel = {
    open_project: 'projects:bulk_siar_export_no_eligible_projects_open',
    close_project: 'projects:bulk_siar_export_no_eligible_projects_close'
} as const satisfies Record<SiarBulkExportType, TKey>

export const siarExportStatusToLabel = {
    pending: 'projects:bulk_siar_export_status_pending',
    partially_transmitted: 'projects:bulk_siar_export_status_partially_transmitted',
    transmitted: 'projects:bulk_siar_export_status_transmitted'
} as const satisfies Record<SiarExportStatus, TKey>

export const siarExportStatusToBadgeVariant = {
    pending: 'orange',
    partially_transmitted: 'danger',
    transmitted: 'success'
} as const satisfies Record<SiarExportStatus, 'danger' | 'orange' | 'success'>

// Per-project transmission status inside an export batch. Shown in the export-detail table
// (vs. `progressStatus`, which is only relevant while composing a new export).
export const siarExportTransmissionStatusToLabel = {
    pending: 'projects:bulk_siar_export_status_pending',
    transmitted: 'projects:bulk_siar_export_status_transmitted'
} as const satisfies Record<SiarExportTransmissionStatus, TKey>

export const siarExportTransmissionStatusToBadgeVariant = {
    pending: 'orange',
    transmitted: 'success'
} as const satisfies Record<SiarExportTransmissionStatus, 'orange' | 'success'>

export const siarPreviewProgressStatusToLabel = {
    to_print: 'projects:status_to_print',
    to_export_opening: 'projects:status_to_export_opening',
    to_transmit_opening: 'projects:status_to_transmit_opening',
    to_export_closing: 'projects:status_to_export_closing',
    to_transmit_closing: 'projects:status_to_transmit_closing'
} as const satisfies Record<SiarPreviewItemProgressStatus, TKey>

export const siarPreviewProgressStatusToBadgeVariant = {
    to_print: 'primary',
    to_export_opening: 'neutral',
    to_transmit_opening: 'orange',
    to_export_closing: 'neutral',
    to_transmit_closing: 'orange'
} as const satisfies Record<SiarPreviewItemProgressStatus, 'neutral' | 'orange' | 'primary' | 'success'>

// Known exclusion reasons mapped to their translation key. The enum is extensible on the
// BE, so unknown values fall back to a generic label via resolveExclusionReasonLabel.
export const exclusionReasonToLabel = {
    missing_initial_evaluation_access: 'projects:siar_exclusion_reason_missing_initial_evaluation_access',
    opening_not_transmitted: 'projects:siar_exclusion_reason_opening_not_transmitted'
} as const satisfies Record<KnownExclusionReason, TKey>

export const EXCLUSION_REASON_FALLBACK_KEY: TKey = 'projects:siar_exclusion_reason_generic'

export const resolveExclusionReasonLabel = (reason: string): TKey =>
    (exclusionReasonToLabel as Record<string, TKey>)[reason] ?? EXCLUSION_REASON_FALLBACK_KEY

export const siarExportColumnToLabel = {
    exportType: 'projects:bulk_siar_export_column_type',
    period: 'projects:bulk_siar_export_column_period',
    createdAtAndAuthor: 'projects:bulk_siar_export_column_created_at',
    status: 'projects:bulk_siar_export_column_status',
    transmittedAt: 'projects:bulk_siar_export_column_transmitted_at',
    name: 'projects:bulk_siar_export_column_name',
    moreActions: 'commons:actions'
} as const satisfies Record<SiarExportsColumn, TKey>
